import React, { useEffect, useRef, useState, useCallback, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, TextField, Button, Select, MenuItem } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import { setOrigin, setDestination, insertWaypointAt, updateWaypoint, updateRouteMarker, selectCurrentRoute, fetchActualTrail, clearActualTrail, selectActualTrail, selectActualTrailLoading } from '../../store/slices/routesSlice';
import { selectUI, setMapClickMode, setNeedsRecalculation, setResetMapView, setDisplayedSavedRoutes, setHoveredSavedLocation } from '../../store/slices/uiSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { TYPE_MAP } from '../../utils/overpassApi';
import { reverseGeocode } from '../../utils/mapboxApi';
import { URBAN_AREAS, fetchTrafficIncidents, INCIDENT_TYPE_MAP } from '../../utils/hereApi';
import { setTrafficIncidents, setTrafficLoading } from '../../store/slices/routesSlice';
import { createLocation, fetchLocations } from '../../store/slices/locationsSlice';
import { AuthContext } from '../../App';
import MapToolbar from './MapToolbar';
import RouteServicesSheet from './RouteServicesSheet';
import RouteReplay from './RouteReplay';

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const hasToken = TOKEN && !TOKEN.includes('PLACEHOLDER');

if (hasToken) {
  mapboxgl.accessToken = TOKEN;
}

const MAPBOX_CTRL_STYLE = `
  .mapboxgl-ctrl-top-right {
    top: 56px !important;
  }
  .mapboxgl-ctrl-group {
    background: rgba(15,23,42,0.18) !important;
    border-radius: 20px !important;
    border: 1px solid rgba(255,255,255,0.12) !important;
    box-shadow: 0 4px 14px rgba(0,0,0,0.20) !important;
    overflow: hidden !important;
    backdrop-filter: blur(12px) !important;
  }
  .mapboxgl-ctrl-group button {
    width: 32px !important;
    height: 32px !important;
    background: transparent !important;
    border: none !important;
  }
  .mapboxgl-ctrl-group button:not(:last-child) {
    border-bottom: 1px solid rgba(255,255,255,0.08) !important;
  }
  .mapboxgl-ctrl-group button:hover {
    background: rgba(255,255,255,0.10) !important;
  }
  .mapboxgl-ctrl-zoom-in .mapboxgl-ctrl-icon {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z'/%3E%3C/svg%3E") !important;
  }
  .mapboxgl-ctrl-zoom-out .mapboxgl-ctrl-icon {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M19 13H5V11H19V13Z'/%3E%3C/svg%3E") !important;
  }
  .mapboxgl-ctrl-compass .mapboxgl-ctrl-icon {
    filter: invert(1) !important;
  }
  .mapboxgl-ctrl-geolocate .mapboxgl-ctrl-icon {
    filter: invert(1) !important;
  }
  .mapboxgl-ctrl-geolocate-active .mapboxgl-ctrl-icon,
  .mapboxgl-ctrl-geolocate-background .mapboxgl-ctrl-icon {
    filter: invert(1) brightness(1.5) sepia(1) hue-rotate(80deg) saturate(4) !important;
  }
  .mapboxgl-ctrl-scale {
    background: rgba(15,23,42,0.75) !important;
    border: 1px solid rgba(255,255,255,0.15) !important;
    border-radius: 8px !important;
    color: rgba(255,255,255,0.85) !important;
    font-size: 10px !important;
    padding: 1px 6px !important;
  }
  .mapboxgl-popup-content {
    border-radius: 12px !important;
    padding: 12px 14px !important;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
  }
  .mapboxgl-popup-close-button {
    font-size: 18px !important;
    padding: 4px 8px !important;
    color: #94a3b8 !important;
  }
  .mapboxgl-popup-close-button:hover {
    color: #0f172a !important;
    background: transparent !important;
  }
  .mapboxgl-popup-tip {
    border-top-color: white !important;
  }
`;

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
    background: '#e8edf3',
  },
  map: {
    width: '100%',
    height: '100%',
  },
  noTokenOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #080d1a 0%, #0d1528 100%)',
    gap: 16,
    zIndex: 10,
  },
  mockMap: {
    width: '100%',
    height: '100%',
    position: 'relative',
    background: 'radial-gradient(ellipse at 30% 60%, #dbeafe 0%, #f0f4f8 60%)',
    overflow: 'hidden',
  },
  clickHint: {
    position: 'absolute',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(59,130,246,0.12)',
    border: '1px solid rgba(59,130,246,0.35)',
    borderRadius: 20,
    padding: '6px 16px',
    backdropFilter: 'blur(10px)',
    pointerEvents: 'none',
    zIndex: 10,
  },
  dragHint: {
    position: 'absolute',
    bottom: 24,
    right: 16,
    background: 'rgba(255,255,255,0.88)',
    border: '1px solid rgba(0,0,0,0.10)',
    borderRadius: 12,
    padding: '4px 12px',
    backdropFilter: 'blur(10px)',
    pointerEvents: 'none',
    zIndex: 10,
  },
}));

// Grid lines for the mock map background
function MockMapGrid() {
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.06 }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#3b82f6" strokeWidth="0.5" />
        </pattern>
        <pattern id="gridLg" width="200" height="200" patternUnits="userSpaceOnUse">
          <path d="M 200 0 L 0 0 0 200" fill="none" stroke="#3b82f6" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
      <rect width="100%" height="100%" fill="url(#gridLg)" />
    </svg>
  );
}

// Returns the stop index if screenPoint is within 32px of a stop marker, -1 otherwise
function findNearStopIdx(routeState, map, screenPoint) {
  const stops = routeState.stops || [];
  const routeCoords = routeState.computedRoute?.geometry?.coordinates;
  if (!stops.length) return -1;
  for (let i = 0; i < stops.length; i++) {
    let coord = stops[i].coordinates; // custom position if dragged off-route
    if (!coord && routeCoords) {
      const idx = Math.min(Math.round(stops[i].progressRatio * (routeCoords.length - 1)), routeCoords.length - 1);
      coord = routeCoords[idx];
    }
    if (!coord) continue;
    const px = map.project(coord);
    const dx = px.x - screenPoint.x;
    const dy = px.y - screenPoint.y;
    if (dx * dx + dy * dy < 32 * 32) return i;
  }
  return -1;
}

// Nearest coordinate on the route geometry to the mouse position (for hover snap)
function findNearestCoord(mouseCoord, routeCoords) {
  let minDist = Infinity, nearest = routeCoords[0];
  for (const coord of routeCoords) {
    const dx = mouseCoord[0] - coord[0];
    const dy = mouseCoord[1] - coord[1];
    const d = dx * dx + dy * dy;
    if (d < minDist) { minDist = d; nearest = coord; }
  }
  return nearest;
}

// Find where along the route (waypoint index) to insert a dragged point
function findInsertionIdx(grabCoord, routeCoords, routeState) {
  if (!routeCoords || routeCoords.length < 2) return 0;
  // Closest polyline index to grab point
  let minDist = Infinity, closestIdx = 0;
  for (let i = 0; i < routeCoords.length; i++) {
    const dx = grabCoord[0] - routeCoords[i][0];
    const dy = grabCoord[1] - routeCoords[i][1];
    const d = dx * dx + dy * dy;
    if (d < minDist) { minDist = d; closestIdx = i; }
  }
  const grabProgress = closestIdx / (routeCoords.length - 1);
  // Build ordered points: origin, waypoints, destination
  const waypoints = (routeState.waypoints || []).filter(w => w.coordinates);
  const allPts = [routeState.origin, ...waypoints, routeState.destination].filter(p => p?.coordinates);
  if (allPts.length < 2) return 0;
  // Find each point's progress along the route geometry
  const ratios = allPts.map(pt => {
    let md = Infinity, idx = 0;
    for (let i = 0; i < routeCoords.length; i++) {
      const dx = pt.coordinates[0] - routeCoords[i][0];
      const dy = pt.coordinates[1] - routeCoords[i][1];
      const d = dx * dx + dy * dy;
      if (d < md) { md = d; idx = i; }
    }
    return idx / (routeCoords.length - 1);
  });
  // Insert before the first waypoint with higher progress than grab point
  for (let i = 1; i < ratios.length - 1; i++) {
    if (grabProgress < ratios[i]) return i - 1;
  }
  return waypoints.length;
}


// ── Vehicle icon helpers ──────────────────────────────────────────────────────
function getVehicleIconInfo(category, course, speed, ignition) {
  const cat = (category || '').toLowerCase().trim();
  // ignition can be: true, false, 'true', 'false', 1, 0, undefined
  const ignOn = ignition === true || ignition === 'true' || ignition === 1 || ignition === '1';
  const color = speed > 0 ? 'green' : ignOn ? 'yellow' : 'red';

  // Standalone / tipos legíveis (ex: "Camião 7.5t") → canvas fallback
  if (!cat || cat.includes(' ')) {
    const ignOn = ignition === true || ignition === 'true' || ignition === 1 || ignition === '1';
    const fb = speed > 0 ? 'vehicle-moving' : ignOn ? 'vehicle-idle' : 'vehicle-off';
    return { iconName: fb, iconRotate: 0 };
  }

  // 3D icons (ex: 3d_vehicle_7): 12 passos de 30°, com correção de rotação
  if (cat.startsWith('3d_')) {
    const steps = 30;
    let snapped = Math.round((course || 0) / steps) * steps;
    if (snapped === 360) snapped = 0;
    const angleIdx = String(snapped).padStart(4, '0');
    const correction = ((course || 0) - snapped + 360) % 360;
    return { iconName: `${cat}_${color}_${angleIdx}`, iconRotate: correction };
  }

  // 2D icons: rotação contínua
  return { iconName: `${cat}_${color}`, iconRotate: course || 0 };
}
// ─────────────────────────────────────────────────────────────────────────────

function MapView() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, lang: authLang } = useContext(AuthContext);
  const lang = authLang || 'pt';
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const routeRef = useRef(null);
  const mapClickModeRef = useRef(null);
  const dragStateRef = useRef({ active: false, insertionIdx: 0 });
  const hoveringRouteRef = useRef(false);
  const measureActiveRef = useRef(false);
  const measurePointsRef = useRef([]);
  const prevHoveredStopRef = useRef(null);
  const prevHoveredMarkerRef = useRef(null);
  const vehiclesFittedRef = useRef(false);
  // Tacho drivers indexed by vehicleId (from /tacho-drivers — same endpoint as Frota).
  // Used to render the popup tacho section with guaranteed parity vs the Frota screen.
  const tachoByVehicleRef = useRef({});

  const [measureActive, setMeasureActive] = useState(false);
  const [trafficVisible, setTrafficVisible] = useState(false);
  const [addFavMode, setAddFavMode] = useState(false);
  const [showVehicles, setShowVehicles] = useState(false);
  const [replayRouteId, setReplayRouteId] = useState(null);
  const addFavModeRef = useRef(false);
  const [pendingFavCoords, setPendingFavCoords] = useState(null);
  const [favName, setFavName] = useState('');
  const [favType, setFavType] = useState('poi');
  const [favAddress, setFavAddress] = useState('');
  const [favSaving, setFavSaving] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [measureTotalKm, setMeasureTotalKm] = useState(0);
  const route = useSelector(selectCurrentRoute);
  const vehicles = useSelector(selectVehicles);
  const actualTrail = useSelector(selectActualTrail);
  const actualTrailLoading = useSelector(selectActualTrailLoading);
  const [showActualTrail, setShowActualTrail] = useState(false);
  const { mapClickMode, showDetails, resetMapView, highlightedPoiId, hoveredStopIdx, hoveredMarkerId, hoveredSavedLocation, displayedSavedRoutes, highlightedStepIdx, hoveredCompareRouteIdx } = useSelector(selectUI);

  // Inject Mapbox control overrides as a real <style> tag so they load after
  // Mapbox GL's own stylesheet (JSS @global injects too early and gets overridden)
  useEffect(() => {
    const id = 'mapbox-ctrl-override';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = MAPBOX_CTRL_STYLE;
    document.head.appendChild(el);
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);

  // Poll /tacho-drivers every 20s so the popup has the same fresh data as the Frota screen
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch('/planner-api/drivers/tacho-drivers', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const list = await res.json();
        if (cancelled || !Array.isArray(list)) return;
        const map = {};
        list.forEach(d => { if (d.vehicleId != null) map[String(d.vehicleId)] = d; });
        tachoByVehicleRef.current = map;
      } catch { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [token]);

  // Keep refs in sync so map event handlers always have fresh state
  useEffect(() => { routeRef.current = route; });
  useEffect(() => { mapClickModeRef.current = mapClickMode; }, [mapClickMode]);

  // Measurement mode: sync ref, update cursor, clear on deactivate
  useEffect(() => {
    measureActiveRef.current = measureActive;
    addFavModeRef.current = addFavMode;
    const map = mapRef.current;
    if (!measureActive) {
      measurePointsRef.current = [];
      setMeasureTotalKm(0);
      if (map && map.getSource('measure-line')) {
        map.getSource('measure-line').setData({ type: 'FeatureCollection', features: [] });
        map.getSource('measure-points').setData({ type: 'FeatureCollection', features: [] });
      }
    }
    if (map && !dragStateRef.current?.active) {
      map.getCanvas().style.cursor = (measureActive || mapClickModeRef.current || addFavMode) ? 'crosshair' : '';
    }
  }, [measureActive, addFavMode]); // eslint-disable-line

  // Escape key exits measure mode
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') setMeasureActive(false); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Update vehicle markers on map (with dynamic EcooGPS icon loading)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource('vehicles')) return;

    const features = vehicles
      .filter(v => v.position)
      .map(v => {
        const speed = v.position.speed || 0;
        const ignition = v.position.attributes?.ignition;
        const { iconName, iconRotate } = getVehicleIconInfo(v.type, v.position.course, speed, ignition);
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [v.position.longitude, v.position.latitude] },
          properties: {
            id: v.id, plate: v.plate, type: v.type, capacity: v.capacity,
            model: v.model || '', fuelType: v.fuelType || '', fuelPer100km: v.fuelPer100km || '',
            tachograph: v.tachograph != null ? v.tachograph : '', phone: v.phone || '',
            speed, course: v.position.course,
            address: v.position.address,
            fixTime: v.position.fixTime,
            power: v.position.attributes?.power,
            hours: v.position.attributes?.hours,
            // Odómetro em metros: preferir io192 (raw Teltonika) → km
            odometer: (v.position.attributes?.io192 ?? v.position.attributes?.odometer) || '',
            ignition,
            ignitionNum: (ignition === true || ignition === 'true' || ignition === 1 || ignition === '1') ? 1 : 0,
            iconName, iconRotate,
            // Tachograph IOs (Teltonika FMC650 — firmware EcooGPS) — mapeamento confirmado
            // contra EcooGPS (14-TF-34 / Nuno Miguel, 2026-04-10) com 5 min de lag
            tachoDriverState: v.position.attributes?.io184 ?? '',
            tachoCardPresent: v.position.attributes?.io10510 ?? '',
            tachoDriverFirstName: v.position.attributes?.io10518 ?? '',
            tachoDriverLastName: v.position.attributes?.io10519 ?? '',
            // io10522 descartado — não é condução contínua (valores não batem com EcooGPS)
            tachoBreakAccum: v.position.attributes?.io10530 ?? '',            // pausa acumulada
            tachoDailyDrive: v.position.attributes?.io10532 ?? '',            // condução diária
            tachoWeeklyDrive: v.position.attributes?.io10508 ?? '',           // condução semanal
            tachoDailyRestRemain: v.position.attributes?.io10534 ?? '',       // descanso diário restante
            tachoNextBreakRemain: v.position.attributes?.io10540 ?? '',       // restante próxima pausa
            tachoNextRestIn: v.position.attributes?.io10541 ?? '',            // próxima pausa/repouso em
            tachoNextBreakDuration: v.position.attributes?.io10528 ?? '',     // duração próxima pausa/repouso
            // Driver associated (via tachograph card or manual)
            currentDriverName: v.currentDriver?.name || '',
            currentDriverPhone: v.currentDriver?.phone || '',
            currentDriverCard: v.currentDriver?.tachoCard || '',
          },
        };
      });

    const geojson = { type: 'FeatureCollection', features };

    // Center map on vehicles once when they first load with positions
    if (features.length > 0 && !vehiclesFittedRef.current) {
      vehiclesFittedRef.current = true;
      if (features.length === 1) {
        map.flyTo({ center: features[0].geometry.coordinates, zoom: 12, duration: 800 });
      } else {
        const bounds = features.reduce(
          (b, f) => b.extend(f.geometry.coordinates),
          new mapboxgl.LngLatBounds(features[0].geometry.coordinates, features[0].geometry.coordinates)
        );
        map.fitBounds(bounds, { padding: { top: 80, bottom: 80, left: 80, right: 80 }, duration: 800, maxZoom: 12 });
      }
    }

    // Only show vehicles when toggle is on
    if (showVehicles) {
      map.getSource('vehicles').setData(geojson);
    } else {
      map.getSource('vehicles').setData({ type: 'FeatureCollection', features: [] });
    }
  }, [vehicles, mapReady, showVehicles]);

  // Initialize map
  useEffect(() => {
    if (!hasToken || !mapContainer.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/standard',
      center: [-8.5, 39.5], // Portugal center
      zoom: 7,
      attributionControl: false,
      dragRotate: false,        // disable right-click rotate (we use right-click for route dragging)
      touchZoomRotate: false,   // disable pinch-rotate on touch
      projection: 'mercator',   // keep flat map at all zoom levels (no globe view)
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: false,
      showUserHeading: false,
      showAccuracyCircle: false,
    }), 'top-right');
    map.addControl(new mapboxgl.ScaleControl({ maxWidth: 100 }), 'bottom-right');

    map.on('load', () => {
      // Set map language to Portuguese
      try {
        // Mapbox GL v3 Standard style — setLanguage is the preferred method
        if (typeof map.setLanguage === 'function') {
          map.setLanguage('pt');
        } else if (map.getStyle()?.name === 'Mapbox Standard') {
          map.setConfigProperty('basemap', 'language', 'pt');
        } else {
          map.getStyle().layers.forEach(layer => {
            if (layer.layout && layer.layout['text-field']) {
              map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', 'name_pt'], ['get', 'name']]);
            }
          });
        }
      } catch(e) { console.warn('Failed to set map language:', e); }

      // Avoid urban areas — fill + dashed border + label (added first so route renders on top)
      map.addSource('avoid-areas', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'avoid-areas-fill',
        type: 'fill',
        source: 'avoid-areas',
        paint: { 'fill-color': '#ef4444', 'fill-opacity': 0.07 },
      });
      map.addLayer({
        id: 'avoid-areas-border',
        type: 'line',
        source: 'avoid-areas',
        layout: { 'line-join': 'round' },
        paint: { 'line-color': '#ef4444', 'line-width': 2, 'line-dasharray': [5, 3], 'line-opacity': 0.8 },
      });
      map.addLayer({
        id: 'avoid-areas-label',
        type: 'symbol',
        source: 'avoid-areas',
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 12,
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-anchor': 'center',
        },
        paint: { 'text-color': '#ef4444', 'text-halo-color': '#ffffff', 'text-halo-width': 2 },
      });

      // Alternative routes source
      map.addSource('route-alts', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      // Glow layer for alternatives
      map.addLayer({
        id: 'route-alts-glow',
        type: 'line',
        source: 'route-alts',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 12,
          'line-blur': 8,
          'line-opacity': ['get', 'glowOpacity'],
        },
      });
      map.addLayer({
        id: 'route-alts-line',
        type: 'line',
        source: 'route-alts',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['get', 'lineWidth'],
          'line-dasharray': [6, 3],
          'line-opacity': ['get', 'opacity'],
        },
      });

      // Main route source and layers
      map.addSource('route', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addLayer({
        id: 'route-glow',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 5,
          'line-blur': 4,
          'line-opacity': 0.2,
        },
      });

      map.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 2,
          'line-opacity': 0.9,
        },
      });

      // Road type colored segments overlaid on route
      map.addSource('route-segments', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'route-segments-line',
        type: 'line',
        source: 'route-segments',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 2, 'line-opacity': 0.9,
        },
      });

      // Step highlight layer (when user clicks a step in route details)
      map.addSource('route-step-highlight', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'route-step-highlight-glow',
        type: 'line',
        source: 'route-step-highlight',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': 12, 'line-blur': 4, 'line-opacity': 0.7,
        },
      });
      map.addLayer({
        id: 'route-step-highlight-line',
        type: 'line',
        source: 'route-step-highlight',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#f43f5e',
          'line-width': 3, 'line-opacity': 1,
        },
      });

      // Invisible wide hitbox on top of route — makes it much easier to click/hover
      map.addLayer({
        id: 'route-line-hitbox',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#3b82f6', 'line-width': 20, 'line-opacity': 0 },
      });

      // Actual GPS trail source + layers (green dashed line for actual driven route)
      map.addSource('actual-trail', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'actual-trail-glow',
        type: 'line',
        source: 'actual-trail',
        layout: { 'line-join': 'round', 'line-cap': 'round', 'visibility': 'none' },
        paint: {
          'line-color': '#22c55e',
          'line-width': 10,
          'line-blur': 6,
          'line-opacity': 0.2,
        },
      });
      map.addLayer({
        id: 'actual-trail-line',
        type: 'line',
        source: 'actual-trail',
        layout: { 'line-join': 'round', 'line-cap': 'round', 'visibility': 'none' },
        paint: {
          'line-color': '#22c55e',
          'line-width': 4,
          'line-dasharray': [4, 4],
          'line-opacity': 0.9,
        },
      });

      // Saved routes overlay source + layers (for displaying multiple saved routes)
      map.addSource('saved-routes', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'saved-routes-glow',
        type: 'line',
        source: 'saved-routes',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 8,
          'line-blur': 5,
          'line-opacity': 0.25,
        },
      });
      map.addLayer({
        id: 'saved-routes-line',
        type: 'line',
        source: 'saved-routes',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 3,
          'line-opacity': 0.85,
        },
      });

      // Measurement tool sources + layers
      map.addSource('measure-line', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'measure-line-layer',
        type: 'line',
        source: 'measure-line',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#f59e0b', 'line-width': 2, 'line-dasharray': [3, 2] },
      });
      map.addSource('measure-points', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'measure-points-layer',
        type: 'circle',
        source: 'measure-points',
        paint: {
          'circle-radius': 5,
          'circle-color': '#ffffff',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#f59e0b',
        },
      });

      // Mapbox traffic layer (hidden by default, rendered below route layers)
      map.addSource('mapbox-traffic', {
        type: 'vector',
        url: 'mapbox://mapbox.mapbox-traffic-v1',
      });
      // Insert traffic below the route glow so route stays visible on top
      const beforeLayer = map.getLayer('route-glow') ? 'route-glow' : undefined;
      map.addLayer({
        id: 'traffic-flow',
        type: 'line',
        source: 'mapbox-traffic',
        'source-layer': 'traffic',
        filter: ['in', ['get', 'congestion'], ['literal', ['moderate', 'heavy', 'severe']]],
        layout: { 'line-join': 'round', 'line-cap': 'round', 'visibility': 'none' },
        paint: {
          'line-color': [
            'match', ['get', 'congestion'],
            'moderate', '#f59e0b',
            'heavy', '#f97316',
            'severe', '#ef4444',
            '#6b7280',
          ],
          'line-width': [
            'interpolate', ['linear'], ['zoom'],
            7, 2,
            10, 4,
            14, 6,
          ],
          'line-opacity': 0.8,
        },
      }, beforeLayer);

      // Vehicle icons — truck rendered to canvas (green=moving, yellow=idle, red=off)
      function createVehicleIcon(color) {
        const vSize = 40;
        const vCanvas = document.createElement('canvas');
        vCanvas.width = vSize;
        vCanvas.height = vSize;
        const vCtx = vCanvas.getContext('2d');
        // White circle background with shadow
        vCtx.beginPath();
        vCtx.arc(vSize / 2, vSize / 2, vSize / 2 - 2, 0, Math.PI * 2);
        vCtx.fillStyle = '#ffffff';
        vCtx.shadowColor = 'rgba(0,0,0,0.25)';
        vCtx.shadowBlur = 4;
        vCtx.fill();
        vCtx.shadowBlur = 0;
        vCtx.strokeStyle = color;
        vCtx.lineWidth = 2.5;
        vCtx.stroke();
        // Truck icon (drawn manually for crisp rendering)
        vCtx.fillStyle = color;
        vCtx.beginPath();
        // Truck body
        vCtx.roundRect(8, 13, 16, 12, 2);
        vCtx.fill();
        // Cabin
        vCtx.beginPath();
        vCtx.roundRect(24, 16, 8, 9, [0, 2, 2, 0]);
        vCtx.fill();
        // Windshield
        vCtx.fillStyle = '#ffffff';
        vCtx.fillRect(26, 18, 5, 4);
        // Wheels
        vCtx.fillStyle = '#0f172a';
        vCtx.beginPath();
        vCtx.arc(14, 26, 2.5, 0, Math.PI * 2);
        vCtx.arc(28, 26, 2.5, 0, Math.PI * 2);
        vCtx.fill();
        // Wheel hubs
        vCtx.fillStyle = '#ffffff';
        vCtx.beginPath();
        vCtx.arc(14, 26, 1, 0, Math.PI * 2);
        vCtx.arc(28, 26, 1, 0, Math.PI * 2);
        vCtx.fill();
        return vCtx.getImageData(0, 0, vSize, vSize);
      }

      const vSize = 40;
      const movingIcon = createVehicleIcon('#10b981');   // Verde — em movimento
      map.addImage('vehicle-moving', { width: vSize, height: vSize, data: movingIcon.data });
      const idleIcon = createVehicleIcon('#f59e0b');     // Amarelo — parado, motor ligado
      map.addImage('vehicle-idle', { width: vSize, height: vSize, data: idleIcon.data });
      const offIcon = createVehicleIcon('#ef4444');      // Vermelho — desligado
      map.addImage('vehicle-off', { width: vSize, height: vSize, data: offIcon.data });

      // Cluster pie-chart via HTML markers (managed in render loop)

      // On-demand icon loading — serve from VPS /vehicle-icons/ (no CORS, Nginx cached)
      map.on('styleimagemissing', (e) => {
        const id = e.id;
        if (id.startsWith('vehicle-') || id.startsWith('cluster-') || id.startsWith('cpie-') || map.hasImage(id)) return;
        // Placeholder imediato para o Mapbox não reclamar
        try { map.addImage(id, { width: vSize, height: vSize, data: offIcon.data }); } catch (_) { return; }
        // 3D: último segmento é ângulo 4 dígitos (ex: "0090"), 2D: última parte é cor
        const is3d = /^\d{4}$/.test(id.split('_').pop());
        const url = is3d ? `/vehicle-icons/3d/${id}.png` : `/vehicle-icons/2d/${id}.png`;
        map.loadImage(url, (err, image) => {
          if (!err && image) {
            try {
              if (map.hasImage(id)) map.removeImage(id);
              map.addImage(id, image);
            } catch (_) { /* ignore */ }
          }
        });
      });

      // Vehicle markers com clustering
      map.addSource('vehicles', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterRadius: 48,
        clusterMaxZoom: 11, // acima deste zoom mostrar ícones individuais
        clusterProperties: {
          // Somar veículos por estado para colorir o cluster
          moving: ['+', ['case', ['>', ['get', 'speed'], 0], 1, 0]],
          idle:   ['+', ['case', ['all', ['==', ['get', 'speed'], 0], ['==', ['get', 'ignitionNum'], 1]], 1, 0]],
          off:    ['+', ['case', ['all', ['==', ['get', 'speed'], 0], ['==', ['get', 'ignitionNum'], 0]], 1, 0]],
        },
      });

      // Cluster pie-chart via canvas-generated icons
      // Generate pie chart icon for cluster based on moving/idle/off proportions
      function generateClusterPieIcon(total, moving, idle, off) {
        const size = total >= 200 ? 64 : total >= 50 ? 56 : 48;
        const r = size / 2;
        const canvas = document.createElement('canvas');
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext('2d');
        const segments = [
          { count: moving, color: '#10b981' },  // verde
          { count: idle,   color: '#f59e0b' },  // amarelo
          { count: off,    color: '#ef4444' },  // vermelho
        ].filter(s => s.count > 0);
        // Draw pie segments (outer ring)
        const ringWidth = 6;
        let startAngle = -Math.PI / 2;
        segments.forEach(seg => {
          const angle = (seg.count / total) * 2 * Math.PI;
          ctx.beginPath();
          ctx.arc(r, r, r - 1, startAngle, startAngle + angle);
          ctx.arc(r, r, r - ringWidth - 1, startAngle + angle, startAngle, true);
          ctx.closePath();
          ctx.fillStyle = seg.color;
          ctx.fill();
          startAngle += angle;
        });
        // White center
        ctx.beginPath();
        ctx.arc(r, r, r - ringWidth - 2, 0, 2 * Math.PI);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        // Number text
        ctx.font = `bold ${total >= 100 ? 12 : 14}px sans-serif`;
        ctx.fillStyle = '#1e293b';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(total), r, r);
        return { data: new Uint8Array(ctx.getImageData(0, 0, size, size).data), width: size, height: size };
      }

      // Use symbol layer for clusters with dynamic pie icons
      map.addLayer({
        id: 'clusters-pie',
        type: 'symbol',
        source: 'vehicles',
        filter: ['has', 'point_count'],
        layout: {
          'icon-image': ['concat', 'cpie-', ['get', 'point_count'], '-', ['get', 'moving'], '-', ['get', 'idle'], '-', ['get', 'off']],
          'icon-allow-overlap': true,
          'icon-size': 1,
        },
      });

      // Generate cluster pie icons on demand
      map.on('styleimagemissing', (e) => {
        const id = e.id;
        if (!id.startsWith('cpie-')) return;
        const parts = id.replace('cpie-', '').split('-').map(Number);
        const [total, moving, idle, off] = parts;
        if (!total || isNaN(total)) return;
        try {
          const icon = generateClusterPieIcon(total, moving || 0, idle || 0, off || 0);
          if (!map.hasImage(id)) map.addImage(id, icon, { pixelRatio: 1 });
        } catch (_) { /* ignore */ }
      });

      // Cluster click handler layer (invisible circle for click/zoom detection)
      map.addLayer({
        id: 'clusters-ring',
        type: 'circle',
        source: 'vehicles',
        filter: ['has', 'point_count'],
        paint: {
          'circle-radius': ['step', ['get', 'point_count'], 22, 50, 26, 200, 32],
          'circle-color': 'transparent',
          'circle-opacity': 0,
        },
      });

      // Veículos individuais (unclustered) — só aparecem quando zoom > clusterMaxZoom
      map.addLayer({
        id: 'vehicles-layer',
        type: 'symbol',
        source: 'vehicles',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': ['coalesce', ['get', 'iconName'], 'vehicle-off'],
          'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.35, 13, 0.55, 16, 0.75],
          'icon-rotate': ['get', 'iconRotate'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-field': ['get', 'plate'],
          'text-size': 10,
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-offset': [0, 2.2],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#0f172a',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      });

      // Drag-routing point — added LAST so it renders on top of all route layers
      map.addSource('drag-point', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'drag-point-layer',
        type: 'circle',
        source: 'drag-point',
        paint: {
          'circle-radius': 8,
          'circle-color': '#ffffff',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#3b82f6',
          'circle-opacity': 1,
        },
      });

      setMapReady(true);
    });

    map.on('click', async (e) => {
      const { lng, lat } = e.lngLat;

      // Add favorite mode — capture coords, reverse geocode, and open dialog
      if (addFavModeRef.current) {
        setPendingFavCoords([lng, lat]);
        setAddFavMode(false);
        setFavName('');
        setFavType('poi');
        setFavAddress('');
        reverseGeocode(lng, lat).then(addr => setFavAddress(addr || ''));
        return;
      }

      // Measurement mode — add point, update layers, recalculate total
      if (measureActiveRef.current) {
        const newPts = [...measurePointsRef.current, [lng, lat]];
        measurePointsRef.current = newPts;
        map.getSource('measure-line').setData({
          type: 'FeatureCollection',
          features: newPts.length > 1 ? [{ type: 'Feature', geometry: { type: 'LineString', coordinates: newPts } }] : [],
        });
        map.getSource('measure-points').setData({
          type: 'FeatureCollection',
          features: newPts.map((pt, i) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: pt }, properties: { i } })),
        });
        let total = 0;
        for (let i = 1; i < newPts.length; i++) {
          const [x1, y1] = newPts[i - 1], [x2, y2] = newPts[i];
          const R = 6371, dLat = (y2 - y1) * Math.PI / 180, dLng = (x2 - x1) * Math.PI / 180;
          const a = Math.sin(dLat / 2) ** 2 + Math.cos(y1 * Math.PI / 180) * Math.cos(y2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
          total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        }
        setMeasureTotalKm(total);
        return;
      }

      const mode = mapClickModeRef.current;

      if (mode === 'origin' || mode === 'destination') {
        const name = await reverseGeocode(lng, lat);
        if (mode === 'origin') {
          dispatch(setOrigin({ name, coordinates: [lng, lat] }));
        } else {
          dispatch(setDestination({ name, coordinates: [lng, lat] }));
        }
        dispatch(setMapClickMode(null));
      } else if (mode?.startsWith('waypoint-')) {
        const idx = parseInt(mode.split('-')[1], 10);
        const name = await reverseGeocode(lng, lat);
        dispatch(updateWaypoint({ index: idx, waypoint: { name, coordinates: [lng, lat] } }));
        dispatch(setMapClickMode(null));
      } else if (mode?.startsWith('marker-')) {
        const markerId = mode.replace('marker-', '');
        const name = await reverseGeocode(lng, lat);
        dispatch(updateRouteMarker({ id: markerId, coordinates: [lng, lat], name }));
        dispatch(setMapClickMode(null));
      }
    });

    // Cluster click — zoom in para ver veículos individuais
    map.on('click', 'clusters-ring', (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const clusterId = f.properties.cluster_id;
      map.getSource('vehicles').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.easeTo({ center: f.geometry.coordinates, zoom: zoom + 1, duration: 500 });
      });
    });
    map.on('mouseenter', 'clusters-ring', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'clusters-ring', () => { map.getCanvas().style.cursor = ''; });

    // Vehicle click — show info popup
    map.on('click', 'vehicles-layer', (e) => {
      if (!e.features?.length) return;
      const f = e.features[0];
      const coords = f.geometry.coordinates.slice();
      const p = f.properties;

      const speedKmh = p.speed != null ? Math.round(p.speed * 1.852) : null;
      const hoursVal = p.hours ? Math.floor(p.hours / 1000 / 3600) : null;
      const powerVal = p.power != null ? (p.power).toFixed(1) : null;

      // Compute parked duration from fixTime
      let statusText = '';
      if (p.fixTime && speedKmh === 0) {
        const diff = Date.now() - new Date(p.fixTime).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 60) statusText = `Estacionado h\u00e1 ${mins} min`;
        else if (mins < 1440) statusText = `Estacionado h\u00e1 ${Math.floor(mins / 60)} h`;
        else statusText = `Estacionado h\u00e1 ${Math.floor(mins / 1440)} d`;
      } else if (speedKmh > 0) {
        statusText = 'Em movimento';
      }

      const odometerKm = p.odometer ? Math.round(Number(p.odometer) / 1000) : null;
      const hasTacho = p.tachograph === true || p.tachograph === 'true' || p.tachograph === 1 ||
        (p.tachograph !== false && p.tachograph !== 'false' && p.tachograph !== 0 && p.type && /truck|hgv|camião|cisterna|frig|semi|trailer|reboque/i.test(p.type));

      // Build tachograph section — reads from tachoByVehicleRef (populated via /tacho-drivers
      // poll), SAME source as the Frota screen to guarantee identical values
      const tacho = tachoByVehicleRef.current[String(p.id)] || null;
      const ds = tacho?.activity != null ? Number(tacho.activity) : null;
      const driverStates = { 0: 'Descanso', 1: 'Disponível', 2: 'Trabalho', 3: 'Condução' };
      const driverStateColors = { 0: '#94a3b8', 1: '#3b82f6', 2: '#f59e0b', 3: '#10b981' };
      const fmtMin = (m) => { if (m === '' || m === null || m === undefined) return '—'; const v = Number(m); const h = Math.floor(v / 60); const mm = String(v % 60).padStart(2, '0'); return `${String(h).padStart(2, '0')}:${mm}`; };
      const tachoRow = (label, val) => val !== '' && val !== undefined && val !== null ? `<div style="display:flex;justify-content:space-between;padding:2px 0;font-size:11px"><span style="color:#475569">${label}:</span><span style="font-weight:600;color:#0f172a;margin-left:12px;white-space:nowrap">${val}</span></div>` : '';

      const driverName = tacho?.name || p.currentDriverName || '';
      const hasTachoSection = hasTacho || !!tacho || driverName;

      const tachoHtml = !hasTachoSection ? '' : `
        <div style="border-left:1px solid #e2e8f0;padding-left:14px;min-width:290px">
          <div style="font-weight:700;font-size:13px;color:#0f172a;margin-bottom:8px">TACÓGRAFO</div>
          ${ds != null ? tachoRow('Actividade actual', `<span style="color:${driverStateColors[ds] || '#475569'};font-weight:700">${driverStates[ds] || 'Desconhecido'}</span>`) : ''}
          ${odometerKm ? tachoRow('Odómetro', `${odometerKm.toLocaleString()} km`) : ''}
          ${tacho && (tacho.cardInserted || ds === 2 || ds === 3)
            ? tachoRow('Cartão inserido', tacho.cardInserted ? '<span style="color:#10b981">Sim</span>' : '<span style="color:#ef4444">Não</span>')
            : ''}
          ${driverName ? `<div style="font-size:11px;font-weight:600;color:#0f172a;padding:4px 0;text-align:right">${driverName}</div>` : ''}
          ${tacho ? '<hr style="border:none;border-top:1px solid #e2e8f0;margin:6px 0"/>' : ''}
          ${tacho ? tachoRow('Tempo de pausa acumulada', fmtMin(tacho.breakAccum)) : ''}
          ${tacho ? tachoRow('Tempo de condução diária', fmtMin(tacho.dailyDriveMin)) : ''}
          ${tacho ? tachoRow('Tempo de condução semanal', fmtMin(tacho.weeklyDriveMin)) : ''}
          ${tacho ? tachoRow('Descanso diário restante', fmtMin(tacho.dailyRest)) : ''}
          ${tacho ? tachoRow('Tempo restante próxima pausa', fmtMin(tacho.nextBreak)) : ''}
          ${tacho ? tachoRow('Próxima pausa/repouso em', fmtMin(tacho.nextRestIn)) : ''}
          ${tacho ? tachoRow('Duração próxima pausa/repouso', fmtMin(tacho.nextRestDuration)) : ''}
        </div>`;

      // Vehicle status color for popup
      const ignOn = p.ignition === true || p.ignition === 'true' || p.ignition === 1 || p.ignition === '1' || Number(p.ignitionNum) === 1;
      const vStatusColor = speedKmh > 0 ? '#10b981' : ignOn ? '#f59e0b' : '#ef4444';
      const vStatusBg = speedKmh > 0 ? '#dcfce7' : ignOn ? '#fef3c7' : '#fee2e2';
      const vStatusLabel = speedKmh > 0 ? 'Em condução' : ignOn ? 'Ralenti' : (statusText || 'Estacionado');

      const html = `
        <div style="font-family:'Inter',sans-serif;display:flex;gap:0;padding:4px">
          <div style="min-width:200px;${hasTachoSection ? 'padding-right:14px;' : ''}">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
              <div style="width:10px;height:10px;border-radius:50%;background:${vStatusColor};flex-shrink:0;box-shadow:0 0 6px ${vStatusColor}80"></div>
              <div style="font-weight:700;font-size:15px;color:#0f172a">${p.plate || '—'}</div>
            </div>
            ${p.type || p.model ? `<div style="font-size:11px;color:#64748b;margin-bottom:6px">${p.type || ''}${p.model ? (p.type ? ' · ' : '') + p.model : ''}${p.capacity ? ' · ' + p.capacity : ''}</div>` : ''}
            ${p.currentDriverName ? `<div style="font-size:11px;color:#1e40af;margin-bottom:4px;display:flex;align-items:center;gap:4px;font-weight:600"><span>\u{1F464}</span> ${p.currentDriverName}${p.currentDriverPhone ? ' · ' + p.currentDriverPhone : ''}</div>` : ''}
            ${p.address ? `<div style="font-size:11px;color:#334155;margin-bottom:4px;display:flex;align-items:flex-start;gap:4px"><span style="flex-shrink:0">\u{1F3E0}</span> ${p.address}</div>` : ''}
            <div style="font-size:10px;color:#94a3b8;margin-bottom:4px;display:flex;align-items:center;gap:6px">
              <span>\u{1F30D}</span> ${coords[1].toFixed(6)}, ${coords[0].toFixed(6)}
              <a href="https://www.google.com/maps?q=${coords[1]},${coords[0]}" target="_blank" rel="noopener noreferrer" style="color:#3b82f6;text-decoration:none;font-size:12px" title="Google Maps">\u2197</a>
            </div>
            <div style="margin:6px 0;display:flex;align-items:center;gap:6px">
              <span style="font-size:10px;font-weight:600;padding:2px 8px;border-radius:8px;background:${vStatusBg};color:${vStatusColor}">${vStatusLabel}</span>
              ${speedKmh > 0 ? `<span style="font-size:11px;font-weight:700;color:#10b981">${speedKmh} km/h</span>` : ''}
            </div>
            <hr style="border:none;border-top:1px solid #e2e8f0;margin:6px 0"/>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px 16px;font-size:11px">
              ${speedKmh != null ? `<div><span style="color:#94a3b8">Velocidade</span><div style="font-weight:600;color:${speedKmh > 0 ? '#10b981' : '#64748b'}">${speedKmh} km/h</div></div>` : ''}
              ${powerVal ? `<div><span style="color:#94a3b8">Bateria</span><div style="font-weight:600;color:#3b82f6">${powerVal} V</div></div>` : ''}
              ${hoursVal != null ? `<div><span style="color:#94a3b8">Horas motor</span><div style="font-weight:600;color:#8b5cf6">${hoursVal} h</div></div>` : ''}
              ${odometerKm ? `<div><span style="color:#94a3b8">Odómetro</span><div style="font-weight:600;color:#475569">${odometerKm.toLocaleString()} km</div></div>` : ''}
              ${p.fuelType ? `<div><span style="color:#94a3b8">Combustível</span><div style="font-weight:600;color:#475569">${p.fuelType}</div></div>` : ''}
              ${p.fuelPer100km ? `<div><span style="color:#94a3b8">Consumo</span><div style="font-weight:600;color:#475569">${p.fuelPer100km} L/100km</div></div>` : ''}
            </div>
            ${p.phone ? `<div style="margin-top:6px;font-size:11px;color:#64748b">\u{1F4DE} ${p.phone}</div>` : ''}
          </div>
          ${tachoHtml}
        </div>
      `;

      new mapboxgl.Popup({ offset: 15, maxWidth: hasTachoSection ? '560px' : '280px', closeButton: true })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(map);
    });

    // Cursor pointer on vehicle hover
    map.on('mouseenter', 'vehicles-layer', () => {
      if (!mapClickModeRef.current && !measureActiveRef.current) map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'vehicles-layer', () => {
      if (!mapClickModeRef.current && !measureActiveRef.current) map.getCanvas().style.cursor = '';
    });

    // Global mousemove: during active drag, move circle freely with the mouse
    map.on('mousemove', (e) => {
      if (dragStateRef.current.active) {
        const src = map.getSource('drag-point');
        if (src) src.setData({
          type: 'FeatureCollection',
          features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: [e.lngLat.lng, e.lngLat.lat] } }],
        });
        return;
      }
      if (!mapClickModeRef.current && !hoveringRouteRef.current) {
        map.getCanvas().style.cursor = '';
      }
    });

    // Hover over route hitbox: show snapping circle + grab cursor
    map.on('mousemove', 'route-line-hitbox', (e) => {
      if (dragStateRef.current.active) return;
      hoveringRouteRef.current = true;
      if (mapClickModeRef.current) return;
      const rt = routeRef.current;
      if (!rt?.computedRoute?.geometry) return;
      // Near a stop marker → hide drag ball (stop interaction is on the DOM element)
      if (findNearStopIdx(rt, map, e.point) >= 0) {
        const src = map.getSource('drag-point');
        if (src) src.setData({ type: 'FeatureCollection', features: [] });
        map.getCanvas().style.cursor = 'grab';
        return;
      }
      const nearest = findNearestCoord([e.lngLat.lng, e.lngLat.lat], rt.computedRoute.geometry.coordinates);
      const src = map.getSource('drag-point');
      if (src) src.setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: nearest } }],
      });
      map.getCanvas().style.cursor = 'grab';
    });
    map.on('mouseleave', 'route-line-hitbox', () => {
      if (dragStateRef.current.active) return;
      hoveringRouteRef.current = false;
      const src = map.getSource('drag-point');
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      map.getCanvas().style.cursor = mapClickModeRef.current ? 'crosshair' : '';
    });

    // Prevent browser context menu on right-click (used for route dragging)
    map.getCanvas().addEventListener('contextmenu', ev => ev.preventDefault());

    // Left or right-click drag on route: adds via waypoint, or moves origin/destination if near endpoint
    map.on('mousedown', 'route-line-hitbox', (e) => {
      if (e.originalEvent.button !== 0 && e.originalEvent.button !== 2) return; // left or right click only
      const rt = routeRef.current;
      if (!rt?.computedRoute?.geometry) return;

      // Near a stop: don't start route drag — stop drag is handled on the marker DOM element
      if (findNearStopIdx(rt, map, e.point) >= 0) return;

      e.preventDefault();

      const grabCoord = [e.lngLat.lng, e.lngLat.lat];

      // Detect if grabbing near origin or destination endpoint (within 30px screen distance)
      let dragMode = 'waypoint';
      if (rt.destination?.coordinates) {
        const destPx = map.project(rt.destination.coordinates);
        const dx = destPx.x - e.point.x;
        const dy = destPx.y - e.point.y;
        if (Math.sqrt(dx * dx + dy * dy) < 30) dragMode = 'destination';
      }
      if (dragMode === 'waypoint' && rt.origin?.coordinates) {
        const origPx = map.project(rt.origin.coordinates);
        const dx = origPx.x - e.point.x;
        const dy = origPx.y - e.point.y;
        if (Math.sqrt(dx * dx + dy * dy) < 30) dragMode = 'origin';
      }

      const insertionIdx = dragMode === 'waypoint'
        ? findInsertionIdx(grabCoord, rt.computedRoute.geometry.coordinates, rt)
        : 0;

      dragStateRef.current = { active: true, insertionIdx, dragMode };
      hoveringRouteRef.current = false;
      map.getCanvas().style.cursor = 'grabbing';
      map.dragPan.disable();
    });
    map.on('mouseup', async (e) => {
      if (!dragStateRef.current.active) return;
      const { insertionIdx, dragMode } = dragStateRef.current;

      // Stop drags are handled entirely by pointer capture on the marker element
      if (dragMode === 'stop') return;

      dragStateRef.current = { active: false, insertionIdx: 0, dragMode: 'waypoint' };
      map.getCanvas().style.cursor = '';
      map.dragPan.enable();
      // Clear drag circle
      const src = map.getSource('drag-point');
      if (src) src.setData({ type: 'FeatureCollection', features: [] });

      // Reverse geocode drop position and dispatch
      const dropCoord = [e.lngLat.lng, e.lngLat.lat];
      const name = await reverseGeocode(dropCoord[0], dropCoord[1]);
      if (dragMode === 'destination') {
        dispatch(setDestination({ name, coordinates: dropCoord }));
      } else if (dragMode === 'origin') {
        dispatch(setOrigin({ name, coordinates: dropCoord }));
      } else {
        dispatch(insertWaypointAt({ index: insertionIdx, waypoint: { name, coordinates: dropCoord } }));
      }
      dispatch(setNeedsRecalculation(true));
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [hasToken]); // eslint-disable-line

  // Update route on map when computedRoute/alternatives change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const source = map.getSource('route');
    const altSource = map.getSource('route-alts');
    if (!source || !altSource) return;

    if (route.computedRoute?.geometry) {
      source.setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: route.computedRoute.geometry }],
      });

      // Road type colored segments overlaid on route
      const ROAD_COLORS = { motorway: '#f59e0b', highway: '#8b5cf6', national: '#ef4444', local: '#3b82f6' };
      const segSource = map.getSource('route-segments');
      if (segSource) {
        const allCoords = route.computedRoute.geometry.coordinates;
        const rawSegments = route.computedRoute.roadSegments || [];

        // Merge consecutive segments of the same type
        const merged = [];
        for (const seg of rawSegments) {
          const last = merged[merged.length - 1];
          if (last && last.type === seg.type && seg.startIdx <= last.endIdx + 1) {
            last.endIdx = Math.max(last.endIdx, seg.endIdx);
          } else {
            merged.push({ ...seg });
          }
        }

        const features = merged
          .map(seg => {
            const start = Math.max(0, seg.startIdx);
            const end = Math.min(seg.endIdx + 1, allCoords.length);
            if (end - start < 3) return null;
            const coords = allCoords.slice(start, end);
            return {
              type: 'Feature',
              properties: { color: ROAD_COLORS[seg.type] || ROAD_COLORS.local },
              geometry: { type: 'LineString', coordinates: coords },
            };
          })
          .filter(Boolean);
        segSource.setData({ type: 'FeatureCollection', features });
      }

      // Show alternatives as dashed gray lines
      const ALT_COLORS = ['#22c55e', '#f59e0b', '#8b5cf6'];
      altSource.setData({
        type: 'FeatureCollection',
        features: (route.alternatives || []).map((alt, i) => ({
          type: 'Feature',
          properties: {
            idx: i + 1,
            color: ALT_COLORS[i] || '#94a3b8',
            opacity: 0.85,
            glowOpacity: 0.25,
            lineWidth: 5,
          },
          geometry: alt.geometry,
        })),
      });

      // Fit bounds — delay 350ms so the details panel finishes opening first
      const coords = route.computedRoute.geometry.coordinates.filter(
        c => c && !isNaN(c[0]) && !isNaN(c[1])
      );
      if (coords.length > 1) {
        const bounds = coords.reduce(
          (b, c) => b.extend(c),
          new mapboxgl.LngLatBounds(coords[0], coords[0])
        );
        setTimeout(() => {
          map.resize();
          map.fitBounds(bounds, {
            padding: { top: 60, bottom: 60, left: 60, right: 60 },
            duration: 1000,
            maxZoom: 14,
          });
        }, 350);
      }
    } else {
      source.setData({ type: 'FeatureCollection', features: [] });
      altSource.setData({ type: 'FeatureCollection', features: [] });
      const segSource = map.getSource('route-segments');
      if (segSource) segSource.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [route.computedRoute, route.alternatives]);

  // Highlight hovered compare route
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    // Main route opacity
    const mainLayer = map.getLayer('route-line');
    const mainGlow = map.getLayer('route-glow');
    if (mainLayer) {
      const mainOpacity = hoveredCompareRouteIdx != null && hoveredCompareRouteIdx !== 0 ? 0.3 : 1;
      map.setPaintProperty('route-line', 'line-opacity', mainOpacity);
      if (mainGlow) map.setPaintProperty('route-glow', 'line-opacity', hoveredCompareRouteIdx != null && hoveredCompareRouteIdx !== 0 ? 0.1 : 0.35);
    }

    // Alternative routes — highlight hovered, dim others
    const altSource = map.getSource('route-alts');
    if (altSource && route.alternatives?.length > 0) {
      const ALT_COLORS = ['#22c55e', '#f59e0b', '#8b5cf6'];
      altSource.setData({
        type: 'FeatureCollection',
        features: (route.alternatives || []).map((alt, i) => {
          const altIdx = i + 1; // 0 is main route
          const isHovered = hoveredCompareRouteIdx === altIdx;
          const isDimmed = hoveredCompareRouteIdx != null && !isHovered;
          return {
            type: 'Feature',
            properties: {
              idx: altIdx,
              color: ALT_COLORS[i] || '#94a3b8',
              opacity: isDimmed ? 0.2 : 0.9,
              glowOpacity: isHovered ? 0.45 : isDimmed ? 0.05 : 0.25,
              lineWidth: isHovered ? 8 : 5,
            },
            geometry: alt.geometry,
          };
        }),
      });
    }
  }, [hoveredCompareRouteIdx, route.alternatives]);

  // Highlight a route step on the map when user clicks it in RouteDetails
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const src = map.getSource('route-step-highlight');
    if (!src) return;

    if (highlightedStepIdx == null || !route.routeSteps || !route.computedRoute?.roadSegments) {
      src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    // Find coordinates for this step by matching step label to road segments
    const step = route.routeSteps[highlightedStepIdx];
    if (!step) { src.setData({ type: 'FeatureCollection', features: [] }); return; }

    const allCoords = route.computedRoute.geometry.coordinates;
    // Accumulate distance through steps to find approximate position
    let accumDist = 0;
    for (let i = 0; i < highlightedStepIdx; i++) {
      accumDist += route.routeSteps[i].distance || 0;
    }
    const stepDist = step.distance || 0;
    const totalDist = route.computedRoute.distance || 1;
    const startFrac = accumDist / totalDist;
    const endFrac = (accumDist + stepDist) / totalDist;
    const startIdx = Math.floor(startFrac * (allCoords.length - 1));
    const endIdx = Math.min(Math.ceil(endFrac * (allCoords.length - 1)) + 1, allCoords.length);

    if (endIdx - startIdx < 2) { src.setData({ type: 'FeatureCollection', features: [] }); return; }

    const coords = allCoords.slice(startIdx, endIdx).filter(c => c && !isNaN(c[0]) && !isNaN(c[1]));
    if (coords.length < 2) { src.setData({ type: 'FeatureCollection', features: [] }); return; }

    src.setData({
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates: coords } }],
    });

    // Fly to the highlighted segment
    const bounds = coords.reduce(
      (b, c) => b.extend(c),
      new mapboxgl.LngLatBounds(coords[0], coords[0])
    );
    map.fitBounds(bounds, { padding: 80, duration: 800, maxZoom: 14 });
  }, [highlightedStepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Saved routes overlay markers ref
  const savedRouteMarkersRef = useRef([]);

  // Display saved routes on map
  useEffect(() => {
    // ALWAYS clean up old markers, regardless of map/source state
    savedRouteMarkersRef.current.forEach(m => m.remove());
    savedRouteMarkersRef.current = [];

    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const source = map.getSource('saved-routes');
    if (!source) return;

    const ROUTE_COLORS = ['#ef4444', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899'];

    if (displayedSavedRoutes && displayedSavedRoutes.length > 0) {
      const features = [];
      const allCoords = [];

      const MARKER_TYPE_CFG = {
        safe:      { color: '#22c55e', emoji: '🛡️' },
        overnight: { color: '#8b5cf6', emoji: '🏨' },
        poi:       { color: '#f59e0b', emoji: '📍' },
        fuel:      { color: '#ef4444', emoji: '⛽' },
      };

      displayedSavedRoutes.forEach((r, idx) => {
        const geom = r.computedRoute?.geometry;
        if (!geom) return;
        const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
        features.push({
          type: 'Feature',
          properties: { color, name: r.name || '', idx },
          geometry: geom,
        });
        if (geom.coordinates) allCoords.push(...geom.coordinates);

        const routeLabel = r.name || `Rota ${idx + 1}`;

        // Origin marker
        if (r.origin?.coordinates) {
          const el = document.createElement('div');
          el.style.cssText = `width:22px;height:22px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;color:#fff;cursor:pointer;`;
          el.textContent = String(idx + 1);
          const popup = new mapboxgl.Popup({ offset: 14, closeButton: false, maxWidth: '200px' })
            .setHTML(`<div style="font-family:Roboto,sans-serif;font-size:11px;color:#0f172a"><strong style="color:${color}">${routeLabel}</strong><br/>🟢 ${r.origin.name || 'Origem'}</div>`);
          const marker = new mapboxgl.Marker({ element: el }).setLngLat(r.origin.coordinates).setPopup(popup).addTo(map);
          savedRouteMarkersRef.current.push(marker);
        }
        // Destination marker
        if (r.destination?.coordinates) {
          const el = document.createElement('div');
          el.style.cssText = `width:16px;height:16px;border-radius:3px;background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);transform:rotate(45deg);cursor:pointer;`;
          const popup = new mapboxgl.Popup({ offset: 14, closeButton: false, maxWidth: '200px' })
            .setHTML(`<div style="font-family:Roboto,sans-serif;font-size:11px;color:#0f172a"><strong style="color:${color}">${routeLabel}</strong><br/>🔴 ${r.destination.name || 'Destino'}</div>`);
          const marker = new mapboxgl.Marker({ element: el }).setLngLat(r.destination.coordinates).setPopup(popup).addTo(map);
          savedRouteMarkersRef.current.push(marker);
        }

        // Waypoint markers
        (r.waypoints || []).forEach((wp, wIdx) => {
          if (!wp.coordinates) return;
          const el = document.createElement('div');
          el.style.cssText = `width:16px;height:16px;border-radius:50%;background:${color}33;border:2px solid ${color};display:flex;align-items:center;justify-content:center;font-size:7px;font-weight:700;color:${color};cursor:pointer;`;
          el.textContent = String(wIdx + 1);
          el.title = `${routeLabel}: ${wp.name || `WP ${wIdx + 1}`}`;
          const marker = new mapboxgl.Marker({ element: el }).setLngLat(wp.coordinates).addTo(map);
          savedRouteMarkersRef.current.push(marker);
        });

        // Stop markers (calculated from progressRatio along route geometry)
        const routeCoords = geom.coordinates;
        (r.stops || []).forEach((stop, sIdx) => {
          const coordIdx = Math.round((stop.progressRatio || 0) * (routeCoords.length - 1));
          const coord = stop.coordinates || routeCoords[Math.min(coordIdx, routeCoords.length - 1)];
          if (!coord) return;
          const el = document.createElement('div');
          el.style.cssText = `width:18px;height:18px;border-radius:50%;background:rgba(245,158,11,0.15);border:2px solid #f59e0b;display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:700;color:#f59e0b;cursor:pointer;`;
          el.textContent = String(sIdx + 1);
          const popup = new mapboxgl.Popup({ offset: 12, closeButton: false, maxWidth: '200px' })
            .setHTML(`<div style="font-family:Roboto,sans-serif;font-size:11px;color:#0f172a">
              <strong style="color:${color}">${routeLabel}</strong><br/>
              🛑 Paragem ${sIdx + 1} · ${stop.duration || '?'}min · Km ${stop.km || '?'}
            </div>`);
          const marker = new mapboxgl.Marker({ element: el }).setLngLat(coord).setPopup(popup).addTo(map);
          savedRouteMarkersRef.current.push(marker);
        });

        // Route markers (safe points, overnight, POIs, fuel)
        (r.routeMarkers || []).forEach((rm) => {
          if (!rm.coordinates) return;
          const cfg = MARKER_TYPE_CFG[rm.type] || { color: '#64748b', emoji: '📍' };
          const el = document.createElement('div');
          el.style.cssText = `width:22px;height:22px;border-radius:50%;background:${cfg.color}22;border:2px solid ${cfg.color};display:flex;align-items:center;justify-content:center;font-size:11px;cursor:pointer;`;
          el.textContent = cfg.emoji;
          const popup = new mapboxgl.Popup({ offset: 14, closeButton: false, maxWidth: '200px' })
            .setHTML(`<div style="font-family:Roboto,sans-serif;font-size:11px;color:#0f172a">
              <strong style="color:${cfg.color}">${cfg.emoji} ${rm.name || 'Sem nome'}</strong>
              ${rm.notes ? `<br/><span style="color:#64748b">${rm.notes}</span>` : ''}
              <br/><span style="color:#94a3b8;font-size:10px">${routeLabel}</span>
            </div>`);
          const marker = new mapboxgl.Marker({ element: el, anchor: 'center' }).setLngLat(rm.coordinates).setPopup(popup).addTo(map);
          savedRouteMarkersRef.current.push(marker);
        });
      });

      source.setData({ type: 'FeatureCollection', features });

      // Fit bounds to all displayed routes
      if (allCoords.length > 1) {
        const bounds = allCoords.reduce(
          (b, c) => b.extend(c),
          new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
        );
        setTimeout(() => {
          map.resize();
          map.fitBounds(bounds, {
            padding: { top: 60, bottom: 60, left: 60, right: 60 },
            duration: 1000,
            maxZoom: 14,
          });
        }, 200);
      }
    } else {
      source.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [displayedSavedRoutes]);

  // Update origin/destination markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear old markers
    Object.values(markersRef.current).forEach(m => m.remove());
    markersRef.current = {};

    if (route.origin?.coordinates) {
      const el = createMarkerEl('#22c55e', 'O');
      markersRef.current.origin = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(route.origin.coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setText(route.origin.name || 'Origem'))
        .addTo(map);
    }

    if (route.destination?.coordinates) {
      const el = createMarkerEl('#ef4444', 'D');
      markersRef.current.destination = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(route.destination.coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setText(route.destination.name || 'Destino'))
        .addTo(map);
    }

    (route.waypoints || []).forEach((wp, idx) => {
      if (wp.coordinates) {
        const el = createMarkerEl('#f59e0b', String(idx + 1));
        markersRef.current[`wp-${idx}`] = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat(wp.coordinates)
          .addTo(map);
      }
    });
  }, [route.origin, route.destination, route.waypoints]);

  // Update stop markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !route.computedRoute) return;

    // Remove old stop markers
    Object.keys(markersRef.current).forEach(key => {
      if (key.startsWith('stop-')) {
        markersRef.current[key].remove();
        delete markersRef.current[key];
      }
    });

    if (!route.stops?.length) return;

    const routeCoords = route.computedRoute.geometry.coordinates;
    route.stops.forEach((stop, idx) => {
      // Use custom coordinates if the stop was dragged off-route, else derive from progressRatio
      const coordIdx = Math.round(stop.progressRatio * (routeCoords.length - 1));
      const coord = stop.coordinates ?? routeCoords[Math.min(coordIdx, routeCoords.length - 1)];
      if (!coord) return;

      const el = createStopMarkerEl(idx + 1, stop.type);

      // Popup opened on hover (not on click) — non-interactive tooltip style
      const popup = new mapboxgl.Popup({ offset: 30, closeButton: false, closeOnClick: false, maxWidth: '220px', focusAfterOpen: false, anchor: 'bottom' });

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat(coord)
        .addTo(map);

      markersRef.current[`stop-${idx}`] = marker;

      // Cache address so we only geocode once per marker lifecycle
      let cachedAddress = null;
      let hideTimer = null;

      const stopTypeLabels = { break_45: 'Pausa 45min', daily_rest: 'Descanso diário (11h)', daily_rest_reduced: 'Descanso reduzido (9h)', weekly_rest: 'Descanso semanal' };
      const stopTypeColors = { break_45: '#f59e0b', daily_rest: '#8b5cf6', daily_rest_reduced: '#a855f7', weekly_rest: '#ef4444' };
      const stopColor = stopTypeColors[stop.type] || '#f59e0b';
      const stopLabel = stop.label || stopTypeLabels[stop.type] || 'Pausa';
      const durStr = stop.duration >= 60 ? `${Math.floor(stop.duration/60)}h${stop.duration%60>0?stop.duration%60+'min':''}` : `${stop.duration}min`;
      function popupHTML(address) {
        return `
          <div style="color:#0f172a;font-family:Roboto,sans-serif;font-size:12px;min-width:180px">
            <strong style="color:${stopColor};font-size:13px">${stopLabel}</strong><br/>
            <span style="color:#475569;font-size:11px">${address || '…'}</span><br/>
            <hr style="border:none;border-top:1px solid #e2e8f0;margin:4px 0"/>
            Duração: <strong>${durStr}</strong> &nbsp;·&nbsp; Km <strong>${stop.km}</strong>
            ${stop.arrivalTime ? `<br/>Chegada: <strong>${stop.arrivalDate ? stop.arrivalDate+' ' : ''}${stop.arrivalTime}</strong>` : ''}
          </div>`;
      }

      // Hover → show popup automatically (without panning the map)
      el.addEventListener('mouseenter', async () => {
        clearTimeout(hideTimer);
        if (!popup.isOpen()) {
          // Temporarily block map movement caused by popup auto-pan
          const origEaseTo = map.easeTo.bind(map);
          const origPanTo = map.panTo.bind(map);
          map.easeTo = () => map;
          map.panTo = () => map;
          popup.setLngLat(coord).setHTML(popupHTML(cachedAddress || 'A carregar…')).addTo(map);
          // Restore after a tick (popup auto-pan fires synchronously)
          requestAnimationFrame(() => {
            map.easeTo = origEaseTo;
            map.panTo = origPanTo;
          });
          // Make popup non-interactive so mouseleave isn't triggered by moving over it
          const popEl = popup.getElement();
          if (popEl) popEl.style.pointerEvents = 'none';
        }
        if (!cachedAddress) {
          cachedAddress = await reverseGeocode(coord[0], coord[1]);
          if (popup.isOpen()) popup.setHTML(popupHTML(cachedAddress));
        }
      });

      // Mouse leaves marker → close popup after short delay
      el.addEventListener('mouseleave', () => {
        hideTimer = setTimeout(() => popup.remove(), 300);
      });

      // Prevent browser context menu on right-click
      el.addEventListener('contextmenu', ev => ev.preventDefault());

      // Left-click drag → move stop to a new position
      // Uses pointer capture so all events stay on this element regardless of cursor position
      el.addEventListener('pointerdown', (domEv) => {
        if (domEv.button !== 0) return;
        domEv.preventDefault(); // prevents compat mousedown from reaching Mapbox canvas
        el.setPointerCapture(domEv.pointerId);

        clearTimeout(hideTimer);
        popup.remove();

        dragStateRef.current = { active: true, dragMode: 'stop', stopIndex: idx, insertionIdx: 0 };
        el.style.cursor = 'grabbing';
        map.getCanvas().style.cursor = 'grabbing';
        map.dragPan.disable();

        let lastDropCoord = null;

        function onMove(ev) {
          const rect = map.getCanvas().getBoundingClientRect();
          try {
            const lngLat = map.unproject([ev.clientX - rect.left, ev.clientY - rect.top]);
            if (lngLat && isFinite(lngLat.lng) && isFinite(lngLat.lat)) {
              lastDropCoord = [lngLat.lng, lngLat.lat];
              const dragSrc = map.getSource('drag-point');
              if (dragSrc) dragSrc.setData({
                type: 'FeatureCollection',
                features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: lastDropCoord } }],
              });
            }
          } catch (_) { /* ignore unproject errors */ }
        }

        async function onUp() {
          el.releasePointerCapture(domEv.pointerId);
          el.removeEventListener('pointermove', onMove);
          el.removeEventListener('pointerup', onUp);
          el.removeEventListener('pointercancel', onCancel);

          el.style.cursor = 'pointer';
          dragStateRef.current = { active: false, insertionIdx: 0, dragMode: 'waypoint' };
          map.getCanvas().style.cursor = '';
          map.dragPan.enable();
          const dragSrc = map.getSource('drag-point');
          if (dragSrc) dragSrc.setData({ type: 'FeatureCollection', features: [] });

          if (!lastDropCoord) return; // no movement — nothing to commit

          // Move marker immediately for instant visual feedback
          const m = markersRef.current[`stop-${idx}`];
          if (m) m.setLngLat(lastDropCoord);

          // Insert a waypoint at the drop position and recalculate the route through it
          const rt = routeRef.current;
          const rcoords = rt?.computedRoute?.geometry?.coordinates;
          const insertionIdx = rcoords ? findInsertionIdx(lastDropCoord, rcoords, rt) : 0;
          const name = await reverseGeocode(lastDropCoord[0], lastDropCoord[1]);
          dispatch(insertWaypointAt({ index: insertionIdx, waypoint: { name, coordinates: lastDropCoord } }));
          dispatch(setNeedsRecalculation(true));
        }

        function onCancel() {
          el.removeEventListener('pointermove', onMove);
          el.removeEventListener('pointerup', onUp);
          el.removeEventListener('pointercancel', onCancel);

          el.style.cursor = 'pointer';
          dragStateRef.current = { active: false, insertionIdx: 0, dragMode: 'waypoint' };
          map.getCanvas().style.cursor = '';
          map.dragPan.enable();
          const dragSrc = map.getSource('drag-point');
          if (dragSrc) dragSrc.setData({ type: 'FeatureCollection', features: [] });
        }

        el.addEventListener('pointermove', onMove);
        el.addEventListener('pointerup', onUp);
        el.addEventListener('pointercancel', onCancel);
      });
    });
  }, [route.stops, route.computedRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update route markers (versao3: safe, overnight, poi, fuel)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove old route markers
    Object.keys(markersRef.current).forEach(key => {
      if (key.startsWith('rm-')) {
        markersRef.current[key].remove();
        delete markersRef.current[key];
      }
    });

    const MARKER_COLORS = {
      safe:      { color: '#22c55e', emoji: '🛡️' },
      overnight: { color: '#8b5cf6', emoji: '🏨' },
      poi:       { color: '#f59e0b', emoji: '📍' },
      fuel:      { color: '#ef4444', emoji: '⛽' },
    };

    (route.routeMarkers || []).forEach((rm) => {
      if (!rm.coordinates) return;
      const cfg = MARKER_COLORS[rm.type] || { color: '#64748b', emoji: '📍' };

      const el = document.createElement('div');
      el.style.cssText = `
        width: 28px; height: 28px; border-radius: 50%;
        background: ${cfg.color}22;
        border: 2px solid ${cfg.color};
        display: flex; align-items: center; justify-content: center;
        cursor: pointer;
        box-shadow: 0 2px 8px ${cfg.color}44;
        font-size: 14px; line-height: 1;
        transition: transform 0.15s;
      `;
      el.textContent = cfg.emoji;
      el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.25)'; });
      el.addEventListener('mouseleave', () => { el.style.transform = 'scale(1)'; });

      const popup = new mapboxgl.Popup({ offset: 16, closeButton: true, maxWidth: '200px' })
        .setHTML(`
          <div style="font-family:Roboto,sans-serif;font-size:12px;color:#0f172a;padding:2px 0">
            <strong style="color:${cfg.color}">${cfg.emoji} ${rm.name || 'Sem nome'}</strong>
            ${rm.notes ? `<br/><span style="color:#64748b;font-size:11px">${rm.notes}</span>` : ''}
          </div>
        `);

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat(rm.coordinates)
        .setPopup(popup)
        .addTo(map);

      markersRef.current[`rm-${rm.id}`] = marker;
    });
  }, [route.routeMarkers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update avoid-urban-areas polygons on map
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const source = map.getSource('avoid-areas');
    if (!source) return;
    const selected = route.routeOptions?.avoidUrbanAreas || [];
    const features = selected.map(city => {
      const ring = URBAN_AREAS[city];
      if (!ring) return null;
      return { type: 'Feature', properties: { name: city }, geometry: { type: 'Polygon', coordinates: [ring] } };
    }).filter(Boolean);
    source.setData({ type: 'FeatureCollection', features });
  }, [route.routeOptions?.avoidUrbanAreas]); // eslint-disable-line

  // Reset map to initial view when route is cleared
  useEffect(() => {
    if (!resetMapView) return;
    const map = mapRef.current;
    if (!map) return;
    dispatch(setResetMapView(false));
    // Clear saved routes overlay
    savedRouteMarkersRef.current.forEach(m => m.remove());
    savedRouteMarkersRef.current = [];
    const sr = map.getSource('saved-routes');
    if (sr) sr.setData({ type: 'FeatureCollection', features: [] });
    setTimeout(() => {
      map.resize();
      map.flyTo({ center: [-8.5, 39.5], zoom: 7, duration: 800 });
    }, 50);
  }, [resetMapView]); // eslint-disable-line

  // Sidebar stop hover → open/close popup on the corresponding map marker
  useEffect(() => {
    const prev = prevHoveredStopRef.current;
    prevHoveredStopRef.current = hoveredStopIdx;

    // Close only the previously hovered stop (avoids timer collisions)
    if (prev !== null && prev !== undefined && prev !== hoveredStopIdx) {
      const prevEl = markersRef.current[`stop-${prev}`]?.getElement?.();
      if (prevEl) prevEl.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
    }
    if (hoveredStopIdx === null || hoveredStopIdx === undefined) return;
    const marker = markersRef.current[`stop-${hoveredStopIdx}`];
    if (!marker) return;
    const el = marker.getElement();
    if (el) el.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
  }, [hoveredStopIdx]); // eslint-disable-line

  // Sidebar route-marker hover → open/close popup on corresponding map marker
  useEffect(() => {
    const prev = prevHoveredMarkerRef.current;
    prevHoveredMarkerRef.current = hoveredMarkerId;

    // Close previous
    if (prev && prev !== hoveredMarkerId) {
      const prevMarker = markersRef.current[`rm-${prev}`];
      if (prevMarker) {
        const popup = prevMarker.getPopup();
        if (popup?.isOpen()) prevMarker.togglePopup();
      }
    }
    if (!hoveredMarkerId) return;
    const marker = markersRef.current[`rm-${hoveredMarkerId}`];
    if (!marker) return;
    const popup = marker.getPopup();
    if (popup && !popup.isOpen()) marker.togglePopup();
    // Fly to marker
    const map = mapRef.current;
    const lngLat = marker.getLngLat();
    if (map && lngLat) {
      map.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: Math.max(map.getZoom(), 11), duration: 600 });
    }
  }, [hoveredMarkerId]); // eslint-disable-line

  // Hover on saved location → show temporary popup on map
  const hoveredSavedPopupRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (hoveredSavedPopupRef.current) {
      hoveredSavedPopupRef.current.remove();
      hoveredSavedPopupRef.current = null;
    }
    if (!hoveredSavedLocation || !map) return;
    const popup = new mapboxgl.Popup({ offset: 10, closeButton: false, maxWidth: '180px' })
      .setLngLat([hoveredSavedLocation.lng, hoveredSavedLocation.lat])
      .setHTML(`<div style="font-size:12px;font-weight:600;color:#0f172a;padding:2px 4px">${hoveredSavedLocation.name}</div>`)
      .addTo(map);
    hoveredSavedPopupRef.current = popup;
  }, [hoveredSavedLocation]); // eslint-disable-line

  // Enhanced glow for round trip routes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('route-glow')) return;
    if (route.roundTrip && route.computedRoute) {
      map.setPaintProperty('route-glow', 'line-width', 18);
      map.setPaintProperty('route-glow', 'line-opacity', 0.45);
      map.setPaintProperty('route-glow', 'line-blur', 8);
    } else {
      map.setPaintProperty('route-glow', 'line-width', 10);
      map.setPaintProperty('route-glow', 'line-opacity', 0.2);
      map.setPaintProperty('route-glow', 'line-blur', 6);
    }
  }, [route.roundTrip, route.computedRoute]); // eslint-disable-line

  // Fly to origin when it is set or changed
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !route.origin?.coordinates) return;
    map.flyTo({ center: route.origin.coordinates, zoom: Math.max(map.getZoom(), 10), duration: 800 });
  }, [route.origin?.coordinates]); // eslint-disable-line

  // Update cursor when mapClickMode changes (skip if drag is active)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || dragStateRef.current?.active) return;
    map.getCanvas().style.cursor = (mapClickMode || addFavMode) ? 'crosshair' : '';
  }, [mapClickMode, addFavMode]);

  function createMarkerEl(color, label) {
    const el = document.createElement('div');
    el.style.cssText = `
      width: 16px; height: 22px; position: relative; cursor: pointer;
      filter: drop-shadow(0 1px 3px rgba(0,0,0,0.35));
    `;
    el.innerHTML = `
      <svg width="16" height="22" viewBox="0 0 16 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 0C3.58 0 0 3.58 0 8c0 6 8 14 8 14s8-8 8-14C16 3.58 12.42 0 8 0z" fill="${color}"/>
        <circle cx="8" cy="7.5" r="4" fill="rgba(255,255,255,0.3)"/>
        <text x="8" y="10" text-anchor="middle" fill="#fff" font-size="7" font-weight="700" font-family="Roboto,sans-serif">${label}</text>
      </svg>
    `;
    return el;
  }

  function createStopMarkerEl(num, stopType) {
    // Color by stop type: break=amber, daily_rest=purple, weekly_rest=red
    const colorMap = {
      break_45: '#f59e0b',
      daily_rest: '#8b5cf6',
      daily_rest_reduced: '#a855f7',
      weekly_rest: '#ef4444',
    };
    const color = colorMap[stopType] || '#f59e0b';
    const size = (stopType && stopType.includes('rest')) ? 28 : 24;
    const icon = (stopType && stopType.includes('daily_rest')) ? '&#x1F319;'
      : stopType === 'weekly_rest' ? '&#x1F6CF;' : '';

    const el = document.createElement('div');
    el.style.cssText = `
      width: ${size}px; height: ${size}px; border-radius: 50%;
      background: ${color}22;
      border: 2px solid ${color};
      display: flex; align-items: center; justify-content: center;
      cursor: pointer;
      box-shadow: 0 0 0 4px ${color}18;
      animation: pulse-stop 2s infinite;
    `;
    el.innerHTML = `
      <style>
        @keyframes pulse-stop {
          0%, 100% { box-shadow: 0 0 0 4px ${color}18; }
          50% { box-shadow: 0 0 0 8px ${color}0a; }
        }
      </style>
      <span style="color:${color};font-size:${size > 24 ? 10 : 9}px;font-weight:700;font-family:Roboto,sans-serif">${icon || num}</span>
    `;
    return el;
  }

  function createPoiMarkerEl(type) {
    const cfg = TYPE_MAP[type] || { emoji: '📍', color: '#64748b' };
    const el = document.createElement('div');
    el.style.cssText = `
      width: 26px; height: 26px; border-radius: 50%;
      background: ${cfg.color}22;
      border: 2px solid ${cfg.color};
      display: flex; align-items: center; justify-content: center;
      cursor: pointer;
      box-shadow: 0 2px 8px ${cfg.color}44;
      font-size: 13px; line-height: 1;
      transition: transform 0.15s;
    `;
    el.textContent = cfg.emoji;
    el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.2)'; });
    el.addEventListener('mouseleave', () => { el.style.transform = 'scale(1)'; });
    return el;
  }

  // ── Actual GPS trail rendering ────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource('actual-trail')) return;

    if (showActualTrail && actualTrail?.trail?.length > 1) {
      const coords = actualTrail.trail.map(p => [p.lng, p.lat]);
      map.getSource('actual-trail').setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
      });
      if (map.getLayer('actual-trail-glow')) map.setLayoutProperty('actual-trail-glow', 'visibility', 'visible');
      if (map.getLayer('actual-trail-line')) map.setLayoutProperty('actual-trail-line', 'visibility', 'visible');
    } else {
      map.getSource('actual-trail').setData({ type: 'FeatureCollection', features: [] });
      if (map.getLayer('actual-trail-glow')) map.setLayoutProperty('actual-trail-glow', 'visibility', 'none');
      if (map.getLayer('actual-trail-line')) map.setLayoutProperty('actual-trail-line', 'visibility', 'none');
    }
  }, [showActualTrail, actualTrail]);

  // Clear actual trail when route changes
  useEffect(() => {
    setShowActualTrail(false);
    dispatch(clearActualTrail());
  }, [route.dbId]); // eslint-disable-line

  // ── Traffic incidents along route ──────────────────────────────────
  const trafficMarkersRef = useRef([]);

  // Fetch incidents when a new route is computed
  useEffect(() => {
    const coords = route.computedRoute?.geometry?.coordinates;
    if (!coords || coords.length < 2) return;
    dispatch(setTrafficLoading(true));
    fetchTrafficIncidents(coords)
      .then(incidents => dispatch(setTrafficIncidents(incidents)))
      .catch(() => dispatch(setTrafficIncidents([])))
      .finally(() => dispatch(setTrafficLoading(false)));
  }, [route.computedRoute]); // eslint-disable-line

  // Render incident markers on map — only visible when traffic toggle is ON
  useEffect(() => {
    // Clean previous
    trafficMarkersRef.current.forEach(m => { m._popup && m._popup.remove(); m.remove(); });
    trafficMarkersRef.current = [];

    const map = mapRef.current;
    if (!map || !trafficVisible || !route.trafficIncidents?.length) return;

    for (const inc of route.trafficIncidents) {
      const cfg = INCIDENT_TYPE_MAP[inc.type] || INCIDENT_TYPE_MAP[4];
      const el = document.createElement('div');
      el.style.cssText = `
        width:26px;height:26px;border-radius:50%;
        background:${cfg.color};border:2px solid #fff;
        display:flex;align-items:center;justify-content:center;
        font-size:13px;cursor:default;box-shadow:0 2px 6px rgba(0,0,0,0.3);
        transition:transform 0.15s;pointer-events:auto;
      `;
      el.textContent = cfg.emoji;

      const popup = new mapboxgl.Popup({ offset: 18, closeButton: false, maxWidth: '240px', closeOnClick: false })
        .setHTML(`
          <div style="font-size:12px;font-weight:600;color:${cfg.color};margin-bottom:2px;">
            ${cfg.emoji} ${cfg.label}
          </div>
          <div style="font-size:11px;color:#475569;">${inc.description}</div>
          ${inc.distKm != null ? `<div style="font-size:10px;color:#94a3b8;margin-top:3px;">${inc.distKm}km da rota</div>` : ''}
        `);

      // Show popup on hover, not click
      el.addEventListener('mouseenter', () => {
        el.style.transform = 'scale(1.3)';
        popup.setLngLat(inc.coordinates).addTo(map);
      });
      el.addEventListener('mouseleave', () => {
        el.style.transform = 'scale(1)';
        popup.remove();
      });
      // Block click from propagating to map (prevents waypoint creation)
      el.addEventListener('click', (e) => { e.stopPropagation(); });

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat(inc.coordinates)
        .addTo(map);

      marker._popup = popup;
      trafficMarkersRef.current.push(marker);
    }
  }, [route.trafficIncidents, trafficVisible]); // eslint-disable-line

  // Highlighted POI marker — shown when user clicks a POI in the details panel
  useEffect(() => {
    const map = mapRef.current;
    // Remove previous highlighted marker
    if (markersRef.current['highlighted-poi']) {
      markersRef.current['highlighted-poi'].remove();
      delete markersRef.current['highlighted-poi'];
    }
    if (!highlightedPoiId || !map) return;
    const rt = routeRef.current;
    const poi = rt?.pois?.find(p => p.id === highlightedPoiId);
    if (!poi) return;
    // Skip POIs inside avoided urban areas
    const avoidedCities = rt?.routeOptions?.avoidUrbanAreas || [];
    const [lng, lat] = poi.coordinates;
    const inAvoided = avoidedCities.some(city => {
      const ring = URBAN_AREAS[city];
      if (!ring) return false;
      const lngs = ring.map(p => p[0]);
      const lats = ring.map(p => p[1]);
      return lng >= Math.min(...lngs) && lng <= Math.max(...lngs) &&
             lat >= Math.min(...lats) && lat <= Math.max(...lats);
    });
    if (inAvoided) return;
    const cfg = TYPE_MAP[poi.type] || { emoji: '📍', color: '#64748b', labelPt: poi.type };
    const el = createPoiMarkerEl(poi.type);
    const popup = new mapboxgl.Popup({ offset: 14, closeButton: true, maxWidth: '200px' })
      .setHTML(`
        <div style="font-family:Roboto,sans-serif;font-size:12px;color:#0f172a;padding:2px 0">
          <strong style="color:${cfg.color}">${cfg.emoji} ${poi.name}</strong><br/>
          <span style="color:#64748b;font-size:11px">${cfg.labelPt}</span>
        </div>
      `);
    const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat(poi.coordinates)
      .setPopup(popup)
      .addTo(map);
    marker.togglePopup();
    markersRef.current['highlighted-poi'] = marker;
    map.flyTo({ center: poi.coordinates, zoom: Math.max(map.getZoom(), 13), duration: 800 });
  }, [highlightedPoiId, route.routeOptions?.avoidUrbanAreas]); // eslint-disable-line

  if (!hasToken) {
    return (
      <Box className={classes.container}>
        <Box className={classes.mockMap}>
          <MockMapGrid />

          {/* Mock "roads" */}
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.15 }}>
            <line x1="20%" y1="80%" x2="80%" y2="20%" stroke="#3b82f6" strokeWidth="2" strokeDasharray="8,4" />
            <line x1="10%" y1="40%" x2="90%" y2="60%" stroke="#6366f1" strokeWidth="1.5" strokeDasharray="6,4" />
            <line x1="50%" y1="10%" x2="40%" y2="90%" stroke="#3b82f6" strokeWidth="1" strokeDasharray="4,4" />
          </svg>

          <Box style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}>
            <Typography style={{
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#0f172a',
              marginBottom: 8,
            }}>
              Mapa não disponível
            </Typography>
            <Typography style={{ fontSize: '0.72rem', color: '#64748b', maxWidth: 300 }}>
              Configure o token Mapbox em <code style={{ color: '#3b82f6' }}>.env</code>
              <br />REACT_APP_MAPBOX_TOKEN=pk.eyJ1...
            </Typography>
            <Box style={{
              marginTop: 16,
              padding: '8px 16px',
              background: 'rgba(59,130,246,0.07)',
              border: '1px solid rgba(59,130,246,0.2)',
              borderRadius: 8,
              display: 'inline-block',
            }}>
              <Typography style={{ fontSize: '0.65rem', color: '#3b82f6' }}>
                A funcionalidade de planeamento de rotas funciona em modo mock
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <div ref={mapContainer} className={classes.map} />
      <MapToolbar />
      {mapClickMode && (
        <Box className={classes.clickHint}>
          <Typography style={{ fontSize: '0.72rem', color: '#3b82f6', whiteSpace: 'nowrap' }}>
            {mapClickMode === 'origin'
              ? '📍 Clique para definir origem'
              : mapClickMode === 'destination'
              ? '🏁 Clique para definir destino'
              : '📌 Clique para definir ponto intermédio'}
          </Typography>
        </Box>
      )}
      {route.computedRoute && !mapClickMode && !measureActive && (
        <Box className={classes.dragHint}>
          <Typography style={{ fontSize: '0.62rem', color: '#64748b', whiteSpace: 'nowrap' }}>
            Arraste a rota para alterar · Clique e arraste nas paragens para as mover
          </Typography>
        </Box>
      )}

      {/* Custom map buttons — right side, below native controls */}
      <Box style={{
        position: 'absolute', top: 210, right: 10, zIndex: 100,
        display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center',
      }}>
      {/* Measure distance button */}
      <Box
        onClick={() => setMeasureActive(a => !a)}
        title={measureActive ? 'Sair da medição (Esc)' : 'Medir distância'}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: measureActive ? 'rgba(245,158,11,0.22)' : 'rgba(15,23,42,0.18)',
          border: `1px solid ${measureActive ? 'rgba(245,158,11,0.55)' : 'rgba(255,255,255,0.12)'}`,
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 14px rgba(0,0,0,0.20)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background 0.2s, border-color 0.2s',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill={measureActive ? '#f59e0b' : 'rgba(255,255,255,0.75)'}>
          <path d="M21 6.5l-3-3-14 14 3 3 14-14zm-14 12l-1.5-1.5 2-2 1.5 1.5-2 2zm3.5-3.5l-1.5-1.5 1-1 1.5 1.5-1 1zm3.5-3.5l-1.5-1.5 1-1 1.5 1.5-1 1zm3.5-3.5l-1.5-1.5 2-2 1.5 1.5-2 2z"/>
        </svg>
      </Box>

      {/* Fit route bounds button — below measure button, only when route exists */}
      {route.computedRoute && (
        <Box
          onClick={() => {
            const map = mapRef.current;
            const coords = route.computedRoute?.geometry?.coordinates;
            if (!map || !coords || coords.length < 2) return;
            const bounds = coords.reduce(
              (b, c) => b.extend(c),
              new mapboxgl.LngLatBounds(coords[0], coords[0])
            );
            map.fitBounds(bounds, {
              padding: { top: 60, bottom: 60, left: 60, right: 60 },
              duration: 800,
              maxZoom: 14,
            });
          }}
          title="Ver rota completa"
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'rgba(15,23,42,0.18)',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.20)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background 0.2s, border-color 0.2s',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="rgba(255,255,255,0.75)">
            <path d="M15 3l2.3 2.3-2.89 2.87 1.42 1.42L18.7 6.7 21 9V3h-6zM3 9l2.3-2.3 2.87 2.89 1.42-1.42L6.7 5.3 9 3H3v6zm6 12l-2.3-2.3 2.89-2.87-1.42-1.42L5.3 17.3 3 15v6h6zm12-6l-2.3 2.3-2.87-2.89-1.42 1.42 2.89 2.87L15 21h6v-6z"/>
          </svg>
        </Box>
      )}

      {/* Traffic layer toggle */}
      <Box
        onClick={() => {
          const map = mapRef.current;
          if (!map) return;
          const next = !trafficVisible;
          setTrafficVisible(next);
          if (map.getLayer('traffic-flow')) {
            map.setLayoutProperty('traffic-flow', 'visibility', next ? 'visible' : 'none');
          }
        }}
        title={trafficVisible ? 'Ocultar trânsito' : 'Mostrar trânsito'}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: trafficVisible ? 'rgba(239,68,68,0.22)' : 'rgba(15,23,42,0.18)',
          border: `1px solid ${trafficVisible ? 'rgba(239,68,68,0.55)' : 'rgba(255,255,255,0.12)'}`,
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 14px rgba(0,0,0,0.20)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background 0.2s, border-color 0.2s',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill={trafficVisible ? '#ef4444' : 'rgba(255,255,255,0.75)'}>
          <path d="M20 10h-3V7c0-1.1-.9-2-2-2h-6c-1.1 0-2 .9-2 2v3H4c-1.1 0-2 .9-2 2v7h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-7c0-1.1-.9-2-2-2zM9 7h6v3H9V7zm-2 12c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm10 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
        </svg>
      </Box>

      {/* Actual trail toggle — only show for in_progress/completed routes with vehicle/driver */}
      {(route.dbStatus === 'in_progress' || route.dbStatus === 'completed') && (route.vehicleId || route.driverId) && (
        <Box
          onClick={() => {
            if (actualTrailLoading) return;
            const next = !showActualTrail;
            setShowActualTrail(next);
            if (next && !actualTrail && route.dbId) {
              dispatch(fetchActualTrail({ token, routeId: route.dbId }));
            }
          }}
          title={showActualTrail ? 'Ocultar rota real' : 'Rota Real (GPS)'}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: showActualTrail ? 'rgba(34,197,94,0.22)' : 'rgba(15,23,42,0.18)',
            border: `1px solid ${showActualTrail ? 'rgba(34,197,94,0.55)' : 'rgba(255,255,255,0.12)'}`,
            backdropFilter: 'blur(12px)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.20)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: actualTrailLoading ? 'wait' : 'pointer',
            userSelect: 'none',
            transition: 'background 0.2s, border-color 0.2s',
          }}
        >
          {actualTrailLoading ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,255,255,0.75)" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill={showActualTrail ? '#22c55e' : 'rgba(255,255,255,0.75)'}>
              <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
            </svg>
          )}
        </Box>
      )}

      {/* Add favorite button */}
      <Box
        onClick={() => setAddFavMode(m => !m)}
        title={addFavMode ? (lang === 'pt' ? 'Cancelar marcação' : 'Cancel marking') : (lang === 'pt' ? 'Marcar favorito no mapa' : 'Mark favorite on map')}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: addFavMode ? 'rgba(234,179,8,0.22)' : 'rgba(15,23,42,0.18)',
          border: `1px solid ${addFavMode ? 'rgba(234,179,8,0.55)' : 'rgba(255,255,255,0.12)'}`,
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 14px rgba(0,0,0,0.20)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background 0.2s, border-color 0.2s',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill={addFavMode ? '#eab308' : 'rgba(255,255,255,0.75)'}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      </Box>

      {/* Add favorite hint */}
      {addFavMode && (
        <Box style={{
          background: 'rgba(234,179,8,0.9)', color: '#fff', padding: '4px 10px', borderRadius: 8,
          fontSize: '0.68rem', fontWeight: 600, backdropFilter: 'blur(8px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          {lang === 'pt' ? 'Clique no mapa para marcar' : 'Click on map to mark'}
        </Box>
      )}
      {/* Replay route trail */}
      {route.computedRoute && route.dbId && (
        <Box
          onClick={() => setReplayRouteId(replayRouteId ? null : route.dbId)}
          title={replayRouteId ? (lang === 'pt' ? 'Fechar replay' : 'Close replay') : (lang === 'pt' ? 'Replay percurso' : 'Replay trail')}
          style={{
            width: 32, height: 32, borderRadius: 8, display: 'flex',
            alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            background: replayRouteId ? 'rgba(245,158,11,0.22)' : 'rgba(15,23,42,0.18)',
            border: `1px solid ${replayRouteId ? 'rgba(245,158,11,0.55)' : 'rgba(255,255,255,0.12)'}`,
            backdropFilter: 'blur(8px)', transition: 'all 0.2s',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill={replayRouteId ? '#f59e0b' : 'rgba(255,255,255,0.75)'}>
            <path d="M8 5v14l11-7z"/>
          </svg>
        </Box>
      )}

      {/* Toggle vehicles visibility */}
      <Box
        onClick={() => setShowVehicles(v => !v)}
        title={showVehicles ? (lang === 'pt' ? 'Esconder veículos' : 'Hide vehicles') : (lang === 'pt' ? 'Mostrar veículos' : 'Show vehicles')}
        style={{
          width: 32, height: 32, borderRadius: 8, display: 'flex',
          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          background: showVehicles ? 'rgba(59,130,246,0.22)' : 'rgba(15,23,42,0.18)',
          border: `1px solid ${showVehicles ? 'rgba(59,130,246,0.55)' : 'rgba(255,255,255,0.12)'}`,
          backdropFilter: 'blur(8px)', transition: 'all 0.2s',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill={showVehicles ? '#3b82f6' : 'rgba(255,255,255,0.75)'}>
          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
        </svg>
      </Box>

      </Box>{/* end custom map buttons container */}

      {/* Add favorite dialog */}
      {pendingFavCoords && (
        <Box style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 200,
          background: '#fff', borderRadius: 14, padding: 20, width: 340, maxWidth: '90vw',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)', border: '1px solid rgba(0,0,0,0.08)',
        }}>
          <Typography style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
            {lang === 'pt' ? 'Adicionar Favorito' : 'Add Favorite'}
          </Typography>
          <Box style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: 10 }}>
            {pendingFavCoords[1].toFixed(6)}, {pendingFavCoords[0].toFixed(6)}
            {favAddress && <div style={{ marginTop: 2, color: '#475569' }}>{favAddress}</div>}
          </Box>
          <input
            placeholder={lang === 'pt' ? 'Nome do favorito' : 'Favorite name'}
            value={favName}
            onChange={e => setFavName(e.target.value)}
            autoFocus
            style={{
              width: '100%', padding: '8px 10px', fontSize: '0.8rem', borderRadius: 8,
              border: '1px solid #e2e8f0', outline: 'none', marginBottom: 8, boxSizing: 'border-box',
            }}
          />
          <select
            value={favType}
            onChange={e => setFavType(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px', fontSize: '0.78rem', borderRadius: 8,
              border: '1px solid #e2e8f0', outline: 'none', marginBottom: 12, boxSizing: 'border-box',
              background: '#fff', color: '#334155',
            }}
          >
            <option value="poi">POI</option>
            <option value="garage">{lang === 'pt' ? 'Garagem' : 'Garage'}</option>
            <option value="safe_point">{lang === 'pt' ? 'Ponto Seguro' : 'Safe Point'}</option>
            <option value="hotel">Hotel</option>
            <option value="fuel">{lang === 'pt' ? 'Combustível' : 'Fuel'}</option>
            <option value="rest_area">{lang === 'pt' ? 'Área de Descanso' : 'Rest Area'}</option>
          </select>
          <Box style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setPendingFavCoords(null)}
              style={{
                flex: 1, padding: '8px 0', fontSize: '0.78rem', fontWeight: 600, borderRadius: 8,
                border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer',
              }}
            >
              {lang === 'pt' ? 'Cancelar' : 'Cancel'}
            </button>
            <button
              disabled={!favName.trim() || favSaving}
              onClick={async () => {
                if (!favName.trim() || !token) return;
                setFavSaving(true);
                try {
                  await dispatch(createLocation({
                    token,
                    location: {
                      name: favName.trim(),
                      type: favType,
                      latitude: pendingFavCoords[1],
                      longitude: pendingFavCoords[0],
                      address: favAddress || null,
                    },
                  })).unwrap();
                  dispatch(fetchLocations(token));
                  setPendingFavCoords(null);
                } catch (err) {
                  console.error('Save favorite error:', err);
                } finally {
                  setFavSaving(false);
                }
              }}
              style={{
                flex: 1, padding: '8px 0', fontSize: '0.78rem', fontWeight: 600, borderRadius: 8,
                border: 'none', background: favName.trim() ? '#3b82f6' : '#94a3b8', color: '#fff',
                cursor: favName.trim() ? 'pointer' : 'not-allowed', opacity: favSaving ? 0.6 : 1,
              }}
            >
              {favSaving ? '...' : (lang === 'pt' ? 'Guardar' : 'Save')}
            </button>
          </Box>
        </Box>
      )}

      {/* Measure hint */}
      {measureActive && measurePointsRef.current.length === 0 && (
        <Box className={classes.clickHint} style={{ borderColor: 'rgba(245,158,11,0.3)' }}>
          <Typography style={{ fontSize: '0.72rem', color: '#f59e0b', whiteSpace: 'nowrap' }}>
            Clique no mapa para medir distância · Esc para sair
          </Typography>
        </Box>
      )}

      {/* Bottom sheet — services along route */}
      <RouteServicesSheet mapRef={mapRef} />

      {/* Route Replay */}
      {replayRouteId && (
        <RouteReplay
          mapRef={mapRef}
          routeId={replayRouteId}
          token={token}
          onClose={() => setReplayRouteId(null)}
          lang={lang}
        />
      )}

      {/* Measure total distance — appears to the left of the button */}
      {measureActive && measureTotalKm > 0 && (
        <Box style={{
          position: 'absolute',
          top: 162,
          right: 52,
          zIndex: 100,
          background: 'rgba(15,23,42,0.75)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(245,158,11,0.45)',
          borderRadius: 8,
          padding: '5px 10px',
          color: '#f59e0b',
          fontSize: '0.78rem',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 14px rgba(0,0,0,0.20)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          {measureTotalKm < 1
            ? `${Math.round(measureTotalKm * 1000)} m`
            : `${measureTotalKm.toFixed(2)} km`}
          <span
            onClick={() => { measurePointsRef.current = []; setMeasureTotalKm(0); const map = mapRef.current; if (map) { map.getSource('measure-line').setData({ type: 'FeatureCollection', features: [] }); map.getSource('measure-points').setData({ type: 'FeatureCollection', features: [] }); } }}
            style={{ fontSize: '0.6rem', color: 'rgba(245,158,11,0.55)', cursor: 'pointer', fontWeight: 400 }}
          >
            limpar
          </span>
        </Box>
      )}
    </Box>
  );
}

export default MapView;
